import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { accessDeniedUrlTree } from '@overa/security';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConsoleCapabilityValue } from './console.model';
import { PlatformService } from '../platform/platform.service';
import { sessionPlatform } from '../platform/platform-session';
import { ConsoleService } from './console.service';

/**
 * Deja entrar solo a quien tenga la capacidad de consola indicada.
 *
 * Es una **segunda** puerta y no la principal: la de verdad la aplica Identity en cada llamada. Esconder
 * una pantalla sin cerrar su API sería teatro; cerrar la API y dejar la pantalla a la vista sería una
 * colección de errores al pulsar. Las dos, y en ese orden de importancia.
 *
 * Quien no la tenga va a acceso denegado, como el resto de denegaciones de la app: redirigir en
 * silencio al lanzador dejaba a la persona sin saber por qué no se abría lo que había pedido.
 */
export function consoleCapabilityGuard(capability: ConsoleCapabilityValue): CanActivateFn {
  return (_route, state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
    const console = inject(ConsoleService);
    const router = inject(Router);

    // Se espera a la respuesta en vez de mirar la señal: el guard puede correr en el primer arranque,
    // antes de que nadie haya preguntado, y ahí la señal vale `null` —«no se sabe»— que negaría el paso
    // a quien sí tiene la capacidad.
    return console.load().pipe(
      map((capabilities) => capabilities.includes(capability) || accessDeniedUrlTree(router, state.url)),
    );
  };
}

/**
 * Only the root global admin (`ConsoleService.isRoot`): the console screens that are not of any platform, like the
 * system's AI. As with the capability guard, this is the second door: the BFF answers 403 to anyone else.
 */
export function consoleRootGuard(): CanActivateFn {
  return (_route, state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
    const console = inject(ConsoleService);
    const router = inject(Router);

    return console.load().pipe(map(() => console.isRoot() || accessDeniedUrlTree(router, state.url)));
  };
}

/**
 * A screen inside a platform for a console operator (docs/OPERADOR-CONTENIDO.md): one of `capabilities` on the open
 * platform. A platform session and the root pass (their screens decide), and so does a route without platform (the
 * shared AI module under /ai, which has its own root guard). The BFF and Identity check again on every call.
 */
export function platformCapabilityGuard(...capabilities: ConsoleCapabilityValue[]): CanActivateFn {
  return (_route, state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
    const console = inject(ConsoleService);
    const platforms = inject(PlatformService);
    const router = inject(Router);

    return console.load().pipe(
      map(() => {
        const code = platforms.active()?.code ?? null;
        if (!code || sessionPlatform() || console.isRoot()) return true;
        return capabilities.some((c) => console.canFor(c, code)) || accessDeniedUrlTree(router, state.url);
      }),
    );
  };
}

/**
 * Console infrastructure (IdP, groups, common services): only for whoever administers platforms (`managesAny`) or the
 * root. A content operator holds «view platforms» and must not see it (docs/OPERADOR-CONTENIDO.md).
 */
export function consoleManagesGuard(): CanActivateFn {
  return (_route, state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
    const console = inject(ConsoleService);
    const router = inject(Router);

    return console.load().pipe(
      map(() => console.isRoot() || console.managesAny() || accessDeniedUrlTree(router, state.url)),
    );
  };
}
